import { colors } from 'Constants/Colors';

export type SessionStatus = 'pending' | 'in_progress' | 'completed';
export type AttendanceStatus = 'present' | 'absent' | 'justified' | null;
export type GroupByOption = 'course' | 'month' | 'instructor' | 'none';

type SessionStatusMeta = { label: string; color: string; bg: string };

const withAlpha = (hex: string, alpha: number): string => {
  // hex like '#00984A' → 'rgba(0, 152, 74, alpha)'
  const v = hex.replace('#', '');
  const bigint = parseInt(v, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Status of a session. Labels in Spanish (project default language).
 * Colors map to the existing Aikon palette in `Constants/Colors`.
 */
export const SESSION_STATUS_META: Record<SessionStatus, SessionStatusMeta> = {
  pending: {
    label: 'Pendiente',
    color: colors.GRAY_DARK,
    bg: withAlpha(colors.GRAY, 0.18),
  },
  in_progress: {
    label: 'En curso',
    color: colors.BLUE,
    bg: withAlpha(colors.BLUE, 0.12),
  },
  completed: {
    label: 'Completada',
    color: colors.GREEN,
    bg: withAlpha(colors.GREEN, 0.12),
  },
};

/**
 * Attendance status of a single employee in a session.
 * `null` represents the "Pendiente" / not-yet-marked state.
 */
export const ATTENDANCE_STATUS_META: Record<'present' | 'absent' | 'justified' | 'pending', SessionStatusMeta> = {
  present: {
    label: 'Asistió',
    color: colors.GREEN,
    bg: withAlpha(colors.GREEN, 0.12),
  },
  absent: {
    label: 'No asistió',
    color: colors.RED,
    bg: withAlpha(colors.RED, 0.1),
  },
  justified: {
    label: 'Justificado',
    color: colors.BROWN,
    bg: withAlpha(colors.YELLOW, 0.18),
  },
  pending: {
    label: 'Pendiente',
    color: colors.GRAY_DARK,
    bg: withAlpha(colors.GRAY, 0.18),
  },
};

export const attendanceMetaFromStatus = (status: AttendanceStatus): SessionStatusMeta =>
  status === null ? ATTENDANCE_STATUS_META.pending : ATTENDANCE_STATUS_META[status];

export const GROUP_BY_LABEL: Record<GroupByOption, string> = {
  course: 'Curso',
  month: 'Mes',
  instructor: 'Instructor',
  none: 'Sin agrupar',
};

/**
 * Color signal for an attendance-percentage figure. Mirrors the mockup
 * tier system (green ≥80, amber 60–79, red <60) but uses the Aikon palette.
 */
export const attendancePercentageColor = (percentage: number): string => {
  if (percentage >= 80) return colors.GREEN;
  if (percentage >= 60) return colors.BROWN;
  return colors.RED;
};
